import { getInvestmentProposalRepository, getInvestmentRepository } from "@/infrastructure/di";
import type {
  InvestmentCreateRequest,
  InvestmentTermsRequest,
  InvestmentUpdateRequest,
} from "@/domain/investment";
import { useCreateMutation } from "../createMutation";
import { investmentCache, toInvestmentPatch } from "./cache";

// Re-export contract and document mutations for barrel compatibility
export {
  useSendReservationContract,
  useSendFinancingContract,
  useSendCommunicationContract,
  usePreviewReservationContract,
  usePreviewFinancingContract,
  usePreviewCommunicationContract,
} from "./contractMutations";
export {
  useUploadDocument,
  useDownloadDocument,
  useReviewDocument,
  useDeleteDocument,
  useViewDocument,
  useDownloadAeatDocumentsMutation,
} from "./documentMutations";

export const useCreateInvestment = (orgId: string) =>
  useCreateMutation({
    mutationFn: (data: InvestmentCreateRequest) => getInvestmentRepository().createInvestment(data),
    cache: (c) => c.create(investmentCache.at(orgId)),
    errorLogMessage: "Failed to create investment:",
  });

/** The investment of an accepted proposal: the backend links the two, so its pending item goes. */
export const useCreateInvestmentFromProposal = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({
      proposalUuid,
      terms,
    }: {
      proposalUuid: string;
      terms: InvestmentTermsRequest;
    }) => getInvestmentProposalRepository().createInvestment(proposalUuid, terms),
    cache: (c) => c.create(investmentCache.at(orgId)),
    errorLogMessage: "Failed to create investment from proposal:",
  });

export const useUpdateInvestment = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: InvestmentUpdateRequest }) =>
      getInvestmentRepository().updateInvestment(uuid, data),
    cache: (c) =>
      c.patch(investmentCache.at(orgId), {
        id: ({ uuid }) => uuid,
        optimistic: ({ data }) => toInvestmentPatch(data),
        fromResponse: (investment) => investment,
        scope: "update",
        keys: (_data, { uuid }) => investmentCache.at(orgId).records(uuid),
      }),
    errorLogMessage: "Failed to update investment:",
  });

export const useBulkUpdateInvestments = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ uuids, data }: { uuids: readonly string[]; data: InvestmentUpdateRequest }) =>
      getInvestmentRepository().bulkUpdateInvestments(uuids, data),
    cache: (c) =>
      c.bulk(investmentCache.at(orgId), {
        action: "patch",
        ids: ({ uuids }) => uuids,
        optimistic: ({ data }) => toInvestmentPatch(data),
        result: (result) => result,
      }),
    errorLogMessage: "Failed to update investments in bulk:",
  });

export const useDeleteInvestment = (orgId: string) =>
  useCreateMutation({
    mutationFn: (uuid: string) => getInvestmentRepository().deleteInvestment(uuid),
    cache: (c) => c.remove(investmentCache.at(orgId), { id: (uuid) => uuid }),
    errorLogMessage: "Failed to delete investment:",
  });

export const useExtendReservation = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: { new_expires_at: string } }) =>
      getInvestmentRepository().extendReservation(uuid, data),
    cache: (c) =>
      c.patch(investmentCache.at(orgId), {
        id: ({ uuid }) => uuid,
        fromResponse: (extension) => ({ reservation_expires_at: extension.new_expires_at }),
      }),
    errorLogMessage: "Failed to extend reservation:",
  });
